<template>
    <div class="preview-buttons-container d-flex">
        <c-button
            v-for="preview in listPreview"
            :key="preview.value"
            class="preview-button d-flex"
            :class="selectedPreview === Number(preview.value) ? 'selected' : 'secondary'"
            @click="onPreviewSelected(preview)"
        >
            <div class="preview-label">
                {{ getPreviewLabel(preview) }}
            </div>
            <div v-if="canModifyPreview" class="preview-actions d-inline-flex align-items-center">
                <font-awesome-icon
                    v-tippy
                    class="ml-3"
                    size="sm"
                    content="Edit"
                    :icon="['fas', 'pencil']"
                    @click="onEditPreview(preview)"
                />
                <font-awesome-icon
                    v-if="listPreview.length > 1"
                    v-tippy
                    class="ml-3"
                    size="sm"
                    content="Delete"
                    :icon="['fas', 'trash']"
                    @click="onDeletePreview(preview)"
                />
            </div>
        </c-button>

        <canvas-toolbar-top-preview-buttons-edit-modal ref="editModalRef" @save="onSavePreview" />
    </div>
</template>

<script lang="ts" setup>
import { computed, ComputedRef, ref, watch, onUnmounted, Ref } from 'vue';
import store from '@/store';
import CButton from '@/components/shared/CButton';
import CanvasToolbarTopPreviewButtonsEditModal from '@/components/DesignStudio/CanvasToolbarTop/previews/edit/CanvasToolbarTopPreviewButtonsEditModal.vue';

type Preview = {
    label: string;
    value: string;
    zIndex: number;
};

const isPreMergeFlow = computed(() => !!store.state.storeLayers?.flags?.isPreMergeFlow);

const canModifyPreview = computed(
    () => !store.state.storeLayers?.flags?.hideMultiPreviewActions && !isPreMergeFlow.value
);
const canvas = computed(() => store.state.storeLayers?.canvas);

const selectedPreview: Ref<number | undefined> = ref();
const editModalRef = ref();
const listVersion = ref(0);
const onPreviewListUpdated = () => {
    listVersion.value++;
};

watch(
    () => canvas.value,
    (newCanvas, oldCanvas) => {
        oldCanvas?.off?.('preview:list:updated', onPreviewListUpdated);
        newCanvas?.on?.('preview:list:updated', onPreviewListUpdated);
        listVersion.value++;
    },
    { immediate: true }
);

onUnmounted(() => {
    canvas.value?.off?.('preview:list:updated', onPreviewListUpdated);
});

const listPreview: ComputedRef<Preview[]> = computed(() => {
    listVersion.value;
    return (
        canvas.value?._listPreview?.map((preview) => ({
            label: preview.name,
            value: preview.PreviewId?.toString(),
            zIndex: preview.zIndex
        })) || []
    );
});

const onPreviewSelected = (preview: Preview) => {
    if (!canvas.value) return;
    if (!preview.value) return;
    canvas.value.selectPreview(Number(preview.value));
};

const onEditPreview = (preview: Preview) => {
    if (editModalRef.value) {
        editModalRef.value.openModal(preview);
    }
};

const onDeletePreview = (preview: Preview) => {
    if (!canvas.value) return;
    if (!preview.value) return;
    canvas.value.deletePreview(Number(preview.value));
};

const onSavePreview = (preview: Preview) => {
    if (!canvas.value) return;
    if (!preview.value) return;
    canvas.value.editPreview(Number(preview.value), {
        name: preview.label
    });
};

const getPreviewLabel = (preview: Preview) => {
    return `${preview.zIndex}. ${preview.label}`;
};

watch(
    () => canvas.value?._selectedPreview,
    () => {
        selectedPreview.value = canvas.value?._selectedPreview?.PreviewId;
    }
);
</script>

<style scoped lang="scss">
@import 'styles/utilities/functions';

.preview-buttons-container {
    overflow: auto;

    .preview-button {
        height: 40px;
        color: get-color('gray-600');
        border-radius: 4px;
        box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.25) !important;
        margin: 0.2rem;
        background-color: #f2f2f2;

        &.selected {
            background-color: get-color('purple') !important;
            color: #fefefe !important;
        }

        &:hover {
            border-color: get-color('purple');
            background-color: #f2f2f2;
            color: get-color('gray-600');
        }

        .preview-label {
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            max-width: 150px;
        }
    }
}
</style>
